// The Items board's just-added highlight (WBS-11904): every line of the last add stays lit until
// that row is pressed (a click or tap anywhere on it, its editor pen, Expand, ⋮), the next add replaces them,
// the tab's Refresh is pressed, or the tab is left (the board, and this state, go with it).
//
// Owns the lit line ids and the grid they paint on, per board mount. Keyed by the board store because the
// profile re-runs on every context change while the grid keeps the options from its first run; state in
// the profile closure would split the add (latest run) from the paint (first run). Not keyed by
// Injector: a component's injector hands out a new wrapper on every inject(Injector).
import { inject } from '@angular/core';
import { BoardHeaderCommandIds } from '@workbuddy/ui/v2/blocks/entity.board/components/board.header/model';
import { EntityBoardStore } from '@workbuddy/ui/v2/blocks/entity.board/state';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import type { GridApi, GridOptions, IRowNode } from 'ag-grid-enterprise';
import { resolveItemRecord } from '../row/item.row.record';

const mounts = new WeakMap<object, JustAddedItemRow>();

/** The highlight of the board whose profile is resolving. Call in the profile's injection context. */
export function injectJustAddedItemRow(): JustAddedItemRow {

  const board = inject(EntityBoardStore, { optional: true });
  if (!board) return new JustAddedItemRow();

  let row = mounts.get(board);
  if (!row) {
    const created = new JustAddedItemRow();
    // Lives as long as the board's own command stream, so it needs no teardown of its own.
    board.commandActions$.subscribe(command => command._id === BoardHeaderCommandIds.Refresh && created.mark([]));
    mounts.set(board, created);
    row = created;
  }

  return row;
}

/** One board's just-added lines: the add adapter marks them, the grid options paint and clear them. */
export class JustAddedItemRow {

  private ids: ReadonlySet<string> = new Set();
  private api: GridApi | null = null;

  /** Lights these lines (empty clears) and repaints the lines they replace. */
  public mark(ids: readonly string[]): void {

    const changed = new Set([...this.ids, ...ids]);
    this.ids = new Set(ids);
    // The add awaits the list refetch, so the new rows are usually painted before their ids arrive here.
    this.redraw(changed);
  }

  /** Grid options that paint the lines, clear each one when its row is pressed, and keep the api for repaints. */
  public withHighlight(options: GridOptions): GridOptions {

    return {
      ...options,
      onGridReady: event => {
        this.api = event.api;
        options.onGridReady?.(event);
      },
      // Press, not row click: the pen and Expand stop their click, so the row never hears it; the press still lands.
      onCellMouseDown: event => {
        options.onCellMouseDown?.(event);
        this.unmark(event.node, event.event?.target);
      },
      getRowStyle: params => {
        // Every painted row passes through here, so the api is known even if grid-ready ran elsewhere.
        this.api ??= params.api;
        const style = options.getRowStyle?.(params);
        if (!this.ids.size || params.node?.group || !this.ids.has(resolveItemRecord(params.data)?._id)) return style;

        return { ...style, background: DesignSystem.tokens.color.background.warningPrimary };
      },
    };
  }

  private unmark(node: IRowNode | null | undefined, target: EventTarget | null | undefined): void {

    const id = resolveItemRecord(node?.data)?._id;
    if (!id || !node?.id || !this.ids.has(id)) return;
    this.ids = new Set([...this.ids].filter(lit => lit !== id));
    // Any AG repaint rebuilds or refreshes the row's cells under the pointer (the ⋮ renderer never refreshes in
    // place), swallowing the click this press began; the background is lifted off the row's elements instead.
    const grid = target instanceof Element ? target.closest('.ag-root-wrapper') : null;
    grid?.querySelectorAll<HTMLElement>(`.ag-row[row-id="${CSS.escape(node.id)}"]`).forEach(row => row.style.removeProperty('background'));
  }

  private redraw(ids: ReadonlySet<string>): void {

    const api = this.api;
    if (!api || api.isDestroyed()) return;

    const rowNodes: IRowNode[] = [];
    api.forEachNode(node => {
      if (!node.group && node.data && ids.has(resolveItemRecord(node.data)?._id)) rowNodes.push(node);
    });
    if (rowNodes.length) api.redrawRows({ rowNodes });
  }
}
